import { CubaCIRuler, LatterAndNumberRuler, NameRuler } from '@dofleini/validator';
import { Col, Form, Input, Row } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import CountrySelect from '@/components/CountrySelect';
import InfoBox from '@/components/InfoBox';
import MunicipalitySelect from '@/components/MunicipalitySelect';
import ProvinceSelect from '@/components/ProvinceSelect';
import { validations } from '@/utils/fields';
import { isIdNumberEqual } from '@/utils/utils';

const MarriedInformation = ({ noTitle, noBorder, isEdit, form, mainIdNumber, dpaRequired }) => {
  const { t } = useTranslation('common');
  const { address, nationality } = Object.assign({
    nationality: 'CU',
    bornPlace: 'CU'
  }, (form?.getFieldValue('civilStatusInfo') || {}));

  const isCuba = useMemo(() => nationality === 'CU', [nationality]);

  const dniValidations = useMemo(() => {
    return isCuba ? [{ required: true }, CubaCIRuler, isIdNumberEqual(mainIdNumber)] : [{ required: true }, isIdNumberEqual(mainIdNumber)];
  }, [isCuba, mainIdNumber]);

  return (
    <div className={classNames({ 'formContainerStyle pt-4 mb-6 px-4': !noBorder }, '')}>
      {!noTitle && <div>
        <InfoBox title={t('civilStatus.information.title')} className="mb-4"/>
      </div>}
      <Row gutter={20}>
        <Col span={12} xs={24} lg={12}>
          <Form.Item
            name={['civilStatusInfo', 'firstName']}
            label={t('civilStatus.information.firstName')}
            rules={[{ required: true }, NameRuler]}
            required>
            <Input size={'large'} placeholder={t('firstNamePlaceHolder')}/>
          </Form.Item>
        </Col>
        <Col span={12} xs={24} lg={12}>
          <Form.Item
            name={['civilStatusInfo', 'middleName']}
            label={t('civilStatus.information.middleName')}
            rules={[NameRuler]}
          >
            <Input size={'large'} placeholder={t('middleNamePlaceHolder')}/>
          </Form.Item>
        </Col>
        <Col span={12} xs={24} lg={12}>
          <Form.Item
            name={['civilStatusInfo', 'lastName']}
            label={t('civilStatus.information.lastName')}
            rules={[{ required: true }, NameRuler]}
            required>
            <Input size={'large'} placeholder={t('lastNamePlaceHolder')}/>
          </Form.Item>
        </Col>
        <Col span={12} xs={24} lg={12}>
          <Form.Item
            name={['civilStatusInfo', 'secondName']}
            label={t('civilStatus.information.secondName')}
            rules={[NameRuler]}
          >
            <Input size={'large'} placeholder={t('secondNamePlaceHolder')}/>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={20}>
        <Col span={12} xs={24} md={12}>
          <Form.Item
            label={t('nationality')}
            name={['civilStatusInfo', 'nationality']}
            rules={[{ required: true }]}
            initialValue={'CU'}>
            <CountrySelect size={'large'}/>
          </Form.Item>
        </Col>
        <Col span={12} xs={24} md={12}>
          <Form.Item
            label={t('bornPlace')}
            name={['civilStatusInfo', 'bornPlace']}
            rules={[{ required: true }]}
            initialValue={'CU'}>
            <CountrySelect size={'large'} placeholder={t('selectPlaceholder')}/>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={20}>
        <Col span={12} xs={24} lg={12}>
          <Form.Item name={['civilStatusInfo', 'idNumber']} label={t('dni.placeholder')} rules={dniValidations}
            required dependencies={[['civilStatusInfo', 'nationality']]}>
            <Input size={'large'} placeholder={t('dniPlaceHolder')} disabled={isEdit}/>
          </Form.Item>
        </Col>
        <Col span={12} xs={24} lg={12}>
          <Form.Item
            name={['civilStatusInfo', 'occupation']}
            label={t('civilStatus.information.occupation')}
            rules={[{ required: true }, LatterAndNumberRuler, validations.text()]}
            required>
            <Input size={'large'} placeholder={t('defaultPlaceholder')}/>
          </Form.Item>
        </Col>
      </Row>
      <div>
        {(isCuba || dpaRequired) && <Row gutter={20}>
          <Col span={12} xs={24} lg={12}>
            <Form.Item
              label={t('province')}
              rules={[{ required: true }]}
              required name={['civilStatusInfo', 'address', 'province']}>
              <ProvinceSelect size={'large'} placeholder={t('selectPlaceholder')}/>
            </Form.Item>
          </Col>
          <Col span={12} xs={24} lg={12}>
            <Form.Item required label={t('municipality')} rules={[{ required: true }]}
              name={['civilStatusInfo', 'address', 'municipality']}
              shouldUpdate={(prevValues, curValues) => curValues?.address?.province}>
              <MunicipalitySelect province={address?.province} size={'large'} placeholder={t('selectPlaceholder')}/>
            </Form.Item>
          </Col>
        </Row>}
        <Form.Item
          label={t('addressResidence')}
          rules={[{ required: true }]}
          required
          name={['civilStatusInfo', 'address', 'abstract']}
        >
          <TextArea maxLength={255} autoSize={{ minRows: 2, maxRows: 5 }} placeholder={t('defaultPlaceholder')}/>
        </Form.Item>
      </div>
    </div>
  );

};

MarriedInformation.propTypes = {
  value: PropTypes.object,
  form: PropTypes.any,
  mainIdNumber: PropTypes.string,
  noTitle: PropTypes.bool,
  isEdit: PropTypes.bool,
  dpaRequired: PropTypes.bool,
  noBorder: PropTypes.bool,
  onChange: PropTypes.func
};

MarriedInformation.defaultProps = {};

export default memo(MarriedInformation);
